import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterGroupType } from '../../../types/filter-group';
import { FilterGroup, logger } from '@fe/core';
import { FilterGroupDto, FilterGroupsService } from '../../../services/axios/axios-api-v2-generated';
import dayjs from 'dayjs';
import { addNotification, NotificationType } from './global-slice';

type FilterGroupsState = Record<FilterGroupType, FilterGroup[]>;

const initialFilterState: FilterGroupsState = {
    [FilterGroupType.airport]: [],
    [FilterGroupType.airspace]: [],
    [FilterGroupType.airway]: [],
    [FilterGroupType.fix]: [],
    [FilterGroupType.flightServiceStation]: [],
    [FilterGroupType.holdingPattern]: [],
    [FilterGroupType.navaid]: [],
    [FilterGroupType.sid]: [],
    [FilterGroupType.star]: [],
    [FilterGroupType.sua]: [],
    [FilterGroupType.weatherStation]: []
} as const;

const initialState = {
    busy: false,
    groups: initialFilterState
};

export const filterGroupsSlice = createSlice({
    name: 'filterGroups',
    initialState: initialState,
    reducers: {
        setFilterGroupsFromList(state, params: { payload: { groups: FilterGroupDto[] } }) {
            for (const key in initialState.groups) {
                state.groups[key as unknown as keyof typeof state.groups] = [];
            }

            for (const group of params.payload.groups) {
                state.groups[group.type!].push({
                    id: group.id!,
                    name: group.name ?? '',
                    members: group.members?.map((x) => ({ text: x.toUpperCase() })) ?? [],
                    isCompany: group.isCompany ?? false,
                    lastUpdate: dayjs(group.lastUpdate, { utc: true }).unix()
                });
            }
        },
        processFilterGroupUpdates(state, params: PayloadAction<{ groups: FilterGroupDto[]; deleted: string[] }>) {
            for (const k in initialState.groups) {
                const type = +k as any as FilterGroupType;
                // Delete
                state.groups[type] = state.groups[type].filter((x) => !params.payload.deleted.includes(x.id));

                const group = state.groups[type];
                for (const update of params.payload.groups.filter((x) => x.type === type)) {
                    const idx = group.findIndex((x) => x.id === update.id);
                    if (idx !== -1) {
                        group[idx].name = update.name ?? '';
                        group[idx].members = update.members?.map((x) => ({ text: x.toUpperCase() })) ?? [];
                        group[idx].isCompany = update.isCompany ?? false;
                        group[idx].lastUpdate = dayjs(update.lastUpdate, { utc: true }).unix();
                    } else {
                        group.push({
                            id: update.id!,
                            name: update.name ?? '',
                            members: update.members?.map((x) => ({ text: x.toUpperCase() })) ?? [],
                            isCompany: update.isCompany ?? false,
                            lastUpdate: dayjs(update.lastUpdate, { utc: true }).unix()
                        });
                    }
                }
            }
        }
    },

    extraReducers: (builder) => {
        // Create
        builder.addCase(createFilterGroup.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(createFilterGroup.fulfilled, (state, action) => {
            state.busy = false;
            const { group, type } = action.payload;
            const groupList = state.groups[type as keyof FilterGroupsState];
            (groupList as FilterGroup[]).push({
                id: group.id!,
                name: group.name ?? '',
                members: group.members?.map((x: string) => ({ text: x.toUpperCase() })) ?? [],
                isCompany: group.isCompany ?? false,
                lastUpdate: dayjs(group.lastUpdate, { utc: true }).unix()
            });
        });
        builder.addCase(createFilterGroup.rejected, (state) => {
            state.busy = false;
        });

        // Modify
        builder.addCase(modifyFilterGroup.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(modifyFilterGroup.fulfilled, (state, action) => {
            state.busy = false;
            const { group, type } = action.payload;
            const groupList = state.groups[type as keyof FilterGroupsState] as FilterGroup[];
            const idx = groupList.findIndex((x: FilterGroup) => x.id === group.id);
            if (idx !== -1) {
                groupList[idx] = {
                    id: group.id!,
                    name: group.name ?? '',
                    members: group.members?.map((x: string) => ({ text: x.toUpperCase() })) ?? [],
                    isCompany: group.isCompany ?? false,
                    lastUpdate: dayjs(group.lastUpdate, { utc: true }).unix()
                };
            }
        });
        builder.addCase(modifyFilterGroup.rejected, (state) => {
            state.busy = false;
        });

        // Delete
        builder.addCase(deleteFilterGroup.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(deleteFilterGroup.fulfilled, (state, action) => {
            state.busy = false;

            for (const type in state.groups) {
                const enumKey = +type as FilterGroupType;
                state.groups[enumKey] = state.groups[enumKey].filter((g) => g.id !== action.payload.groupId);
            }
        });
        builder.addCase(deleteFilterGroup.rejected, (state) => {
            state.busy = false;
        });
    }
});

export const createFilterGroup = createAsyncThunk(
    'filterGroups/createFilterGroup',
    async (params: { group: Partial<FilterGroup>; type: FilterGroupType }, thunkAPI) => {
        try {
            const result = await FilterGroupsService.createFilterGroup(
                {
                    body: {
                        name: params.group.name,
                        type: params.type,
                        members: params.group.members?.map((m) => m.text) ?? [],
                        isCompany: params.group.isCompany ?? false
                    }
                },
                { withAuthorization: true }
            );
            return { group: result, type: params.type };
        } catch (error) {
            thunkAPI.dispatch(addNotification({ type: NotificationType.filterGroupSaveError }));
            return thunkAPI.rejectWithValue(undefined);
        }
    }
);

export const modifyFilterGroup = createAsyncThunk(
    'filterGroups/modifyFilterGroup',
    async (params: { group: FilterGroup; type: FilterGroupType }, thunkAPI) => {
        try {
            const result = await FilterGroupsService.updateFilterGroup(
                {
                    id: params.group.id,
                    body: {
                        name: params.group.name,
                        type: params.type,
                        members: params.group.members.map((m) => m.text.toUpperCase()),
                        isCompany: params.group.isCompany
                    }
                },
                { withAuthorization: true }
            );
            return { group: result, type: params.type };
        } catch (error) {
            thunkAPI.dispatch(addNotification({ type: NotificationType.filterGroupSaveError }));
            return thunkAPI.rejectWithValue(undefined);
        }
    }
);

export const deleteFilterGroup = createAsyncThunk('filterGroups/deleteFilterGroup', async (params: { groupId: string }, thunkAPI) => {
    try {
        await FilterGroupsService.deleteFilterGroup({ id: params.groupId }, { withAuthorization: true });
        return params;
    } catch (error) {
        logger.error('Error deleting filter group', error);
        thunkAPI.dispatch(addNotification({ type: NotificationType.filterGroupSaveError }));
        return thunkAPI.rejectWithValue(undefined);
    }
});

export const { processFilterGroupUpdates, setFilterGroupsFromList } = filterGroupsSlice.actions;
